import { Alert, Dialog, Skeleton } from "@mui/material";
import { useId } from "react";
import { COPY } from "@/constants/campaignContacts";
import { useBudgetAmendments } from "@/hooks/useCampaignContacts";
import { apiErrorMessage } from "@/utils/apiError-helper";
import { budgetHistoryRows } from "./budgetHistory-helper";
import {
  CONTACTS_HISTORY_PAPER_SX,
  CONTACTS_TD_CLASS,
  CONTACTS_TH_CLASS,
  ContactsDialogHeader,
} from "./ContactsDialog";

interface BudgetHistoryModalProps {
  open: boolean;
  onClose: () => void;
  campaignId: string;
  campaignName: string;
  /** The campaign's zone (IANA): the amendment date and the expiry are calendar days in it. */
  timeZone: string;
}

const TD_CLASS = `${CONTACTS_TD_CLASS} whitespace-nowrap`;
const SKELETON_ROWS = [0, 1, 2] as const;

/** Every budget amendment on the campaign, newest first, each figure as "before → after". */
export default function BudgetHistoryModal({
  open,
  onClose,
  campaignId,
  campaignName,
  timeZone,
}: BudgetHistoryModalProps) {
  const T = COPY.budgetHistory;
  const titleId = useId();
  const { data, isLoading, error } = useBudgetAmendments(campaignId, open);
  const rows = budgetHistoryRows(data ?? [], timeZone);
  const columns = [
    T.columns.date,
    T.columns.who,
    T.columns.budget,
    T.columns.deposit,
    T.columns.recipients,
    T.columns.credits,
    T.columns.payment,
    T.columns.expiry,
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      aria-labelledby={titleId}
      slotProps={{ paper: { sx: CONTACTS_HISTORY_PAPER_SX } }}
    >
      <ContactsDialogHeader
        titleId={titleId}
        title={T.title}
        subtitle={T.subtitle(campaignName, rows.length)}
        onClose={onClose}
      />

      {isLoading && (
        <div role="status" aria-busy="true" className="mt-4 flex flex-col gap-2">
          {SKELETON_ROWS.map((row) => (
            <Skeleton key={row} variant="rounded" height={40} />
          ))}
        </div>
      )}

      {!isLoading && error !== null && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {apiErrorMessage(error) || COPY.errorFallback}
        </Alert>
      )}

      {!isLoading && error === null && rows.length === 0 && (
        <p className="mt-6 text-center text-[14px] text-graphite">{T.empty}</p>
      )}

      {!isLoading && error === null && rows.length > 0 && (
        <div className="mt-4 max-h-[56vh] overflow-auto border-t border-smoke">
          <table className="w-full border-collapse">
            <thead>
              <tr>
                {columns.map((label) => (
                  <th key={label} className={CONTACTS_TH_CLASS}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className={`${TD_CLASS} text-rodeo-black`}>{row.when}</td>
                  <td className={`${TD_CLASS} text-graphite`}>{row.who}</td>
                  <td className={`${TD_CLASS} text-rodeo-black`}>{row.budget}</td>
                  <td className={`${TD_CLASS} text-graphite`}>{row.deposit ?? COPY.emptyValue}</td>
                  <td className={`${TD_CLASS} text-graphite`}>{row.recipients}</td>
                  <td className={`${TD_CLASS} text-graphite`}>{row.credits}</td>
                  <td className={`${TD_CLASS} text-graphite`}>{row.payment ?? COPY.emptyValue}</td>
                  <td className={`${TD_CLASS} text-graphite`}>{row.expiry ?? COPY.emptyValue}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Dialog>
  );
}
